/*
  GA4/GTM dataLayer tracking for the booking flow.

  Implements the client tracking spec "Nordiska Kliniken - tracking spec
  bokningsflöde" (2026-07-02): funnel events (steps 1-6) + supporting UX
  events. Funnel events are always sent; when the same event + chosen value
  has already been sent this session the push gets `is_revision: true` so
  GA4 can filter re-selections out of the funnel.

  GTM REQUIREMENT: booking_confirmed is the conversion event and duplicates
  are only *flagged*, never suppressed — the GTM conversion tag MUST exclude
  pushes where is_revision equals true, or conversions will double-count.
  booking_id is a stable per-booking pseudonym (see anonymizedBookingId), so
  it can also be mapped to transaction_id for GA4-native deduplication.

  Dedupe scope caveat: the fired-keys live in sessionStorage, which is
  per-tab, while a GA4 session spans tabs. A user who opens a second
  treatment in a new tab re-fires funnel steps without is_revision within
  the same GA4 session. Accepted trade-off for the navigational steps —
  funnel analysis should use unique-user counts per step, not event counts.
  EXCEPTION: booking_confirmed dedupes via localStorage (opts.crossTab) —
  a completed booking is a cross-tab fact, and the same-device BankID flow
  routinely returns the user in a NEW tab (both tabs reach the confirm
  handler), which sessionStorage cannot catch. An in-memory fallback covers
  browsers where both storages are unavailable (e.g. Safari private mode),
  so dedupe still holds within a page load there.

  Tracking must never break the booking flow — every entry point swallows
  its own errors.
*/

const SS_PREFIX = 'nk_ga4:';

/*
  TTL for the localStorage (crossTab) keys. The BankID new-tab duplicate
  these keys exist to catch happens within minutes; localStorage itself
  never expires, and a key living forever would permanently suppress a
  GENUINE conversion if the same patient cancels a booking and later
  re-books the exact same slot + activity. 24h caps that exposure at
  negligible while comfortably covering the duplicate window.
*/
const CROSS_TAB_TTL_MS = 24 * 60 * 60 * 1000;

/* Fallback when sessionStorage/localStorage is unavailable: dedupe within this page load. */
const memoryFired = new Set();

/* crossTab: use localStorage (shared across tabs, survives the BankID
   app-switch new-tab return) instead of per-tab sessionStorage. */
function hasFired(key, crossTab) {
  if (memoryFired.has(key)) {
    return true;
  }
  try {
    const store = crossTab ? window.localStorage : window.sessionStorage;
    const value = store.getItem(SS_PREFIX + key);
    if (!value) {
      return false;
    }
    if (crossTab) {
      /* Values are Date.now() timestamps — expire and clean up old keys. */
      const firedAt = parseInt(value, 10);
      if (!firedAt || Date.now() - firedAt > CROSS_TAB_TTL_MS) {
        store.removeItem(SS_PREFIX + key);
        return false;
      }
    }
    return true;
  } catch (e) {
    return false;
  }
}

function markFired(key, crossTab) {
  memoryFired.add(key);
  try {
    const store = crossTab ? window.localStorage : window.sessionStorage;
    store.setItem(SS_PREFIX + key, String(Date.now()));
  } catch (e) {}
}

/*
  Push an event to the GTM dataLayer.
  opts.funnel: treat as a funnel event — flag repeats with is_revision.
  opts.dedupeValue: the chosen value the session-dedupe key is built from
  (e.g. the activity name or "YYYY-MM-DD HH:MM" for a slot).
  opts.skipIfFired: drop the push entirely instead of flagging it with
  is_revision — for renders that repeat without any new user choice (e.g.
  the calendar re-rendering on week navigation).
  opts.crossTab: dedupe via localStorage instead of sessionStorage — for
  events where a repeat in ANOTHER tab is still a repeat (booking_confirmed).

  Keys with an `undefined` value are stripped before the push: in GTM's
  data-layer model an explicit undefined OVERWRITES a previously set
  variable, clearing it. `null` is kept — it is meaningful per spec
  (e.g. practitioner_name: null).
*/
export function trackBookingEvent(eventName, params = {}, opts = {}) {
  try {
    window.dataLayer = window.dataLayer || [];
    const payload = { event: eventName };
    Object.keys(params).forEach((key) => {
      if (params[key] !== undefined) {
        payload[key] = params[key];
      }
    });
    if (opts.funnel) {
      const key = eventName + ':' + String(opts.dedupeValue || '');
      if (hasFired(key, opts.crossTab)) {
        if (opts.skipIfFired) {
          return;
        }
        payload.is_revision = true;
      } else {
        markFired(key, opts.crossTab);
      }
    }
    window.dataLayer.push(payload);
  } catch (e) {}
}

/*
  Resolve the city the booking belongs to. Three sources, in order:

  1. cities.selected — the user picked a city in the booking module dropdown.
  2. The selected slot's location_id, looked up in cities.available. This is
     the ONLY source on treatment pages: BookingModuleActivity.vue has no city
     UI at all, and init's auto-select only fires when a single city is
     bookable — so cities.selected stays null for the whole session there.
     It is also the recovery path when a same-device BankID app switch returns
     the user in a NEW tab: the store is fresh and nothing re-selects a city.
  3. booking.cityFromServer — the city the backend resolved from the stored
     order's location id, for that same new-tab case when cities.available
     hasn't finished loading.

  The location lookup mirrors setSelectedSlot's (actions.js) — keep them in
  sync if the cities payload shape changes.
*/
export function resolveBookingCity(state) {
  try {
    if (state.cities && state.cities.selected) {
      return state.cities.selected;
    }
    const slot = state.selectedSlot || {};
    const locationId =
      slot.location_id === undefined || slot.location_id === null
        ? ''
        : String(slot.location_id);
    if (locationId && state.cities && Array.isArray(state.cities.available)) {
      for (const city of state.cities.available) {
        if (
          city &&
          Array.isArray(city.locations) &&
          city.locations.some((l) => l && String(l.id) === locationId)
        ) {
          return city;
        }
      }
    }
    return (state.booking && state.booking.cityFromServer) || null;
  } catch (e) {
    return null;
  }
}

/*
  GA4-friendly slug of the booking city (stockholm, goteborg, skovde) for the
  legacy booking_completed `booking_city` param. Returns undefined when no
  city can be resolved.

  Its three callers push it through a raw window.dataLayer.push, which does
  NOT strip undefined keys the way trackBookingEvent does — so per the note
  above, an unresolvable city there pushes an explicit undefined and can
  clear a booking_city that an earlier push in the same page load had set.
  Pre-existing behaviour of those raw pushes, not something this helper can
  fix; moving the legacy events onto trackBookingEvent would.
*/
export function bookingCitySlug(state) {
  const city = resolveBookingCity(state);
  const raw = (city && (city.slug || city.id || city.name)) || '';
  if (!raw) {
    return undefined;
  }
  try {
    const ascii = String(raw)
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    const cleaned = ascii.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    if (cleaned === 'gothenburg') return 'goteborg';
    return cleaned;
  } catch (e) {
    return String(raw).trim().toLowerCase();
  }
}

/* Common parameters carried by every funnel step (1-6). */
export function funnelParams(state, step) {
  try {
    const activity =
      (state.activities && state.activities.selected) || null;
    /* service_category: prefer the explicitly selected group, but on e.g.
       treatment pages no group is ever selected — fall back to looking up
       the selected activity's group among the available ones. */
    let category =
      (state.activityGroups &&
        state.activityGroups.selected &&
        state.activityGroups.selected.activity_group_name) ||
      undefined;
    if (!category && activity && activity.activity_group_id) {
      const gid = String(activity.activity_group_id);
      if (
        state.activityGroups &&
        Array.isArray(state.activityGroups.available)
      ) {
        const group = state.activityGroups.available.find(
          (g) => g && String(g.activity_group_id) === gid
        );
        category = (group && group.activity_group_name) || undefined;
      }
      /* Treatment pages never populate activityGroups.available (the groups
         UI is unused there) — getActivityGroups({ namesOnly: true }) fills
         this id->name map instead. */
      if (
        !category &&
        state.activityGroups &&
        state.activityGroups.namesById &&
        state.activityGroups.namesById[gid]
      ) {
        category = state.activityGroups.namesById[gid];
      }
    }
    return {
      service_name: (activity && activity.activity_name) || undefined,
      service_category: category,
      city: (resolveBookingCity(state) || {}).name || undefined,
      funnel_step: step,
    };
  } catch (e) {
    return { funnel_step: step };
  }
}

/*
  Stable, non-reversible-ish booking id for GA4 (spec: "Hashat/anonymiserat").
  Built from the real ClinicBuddy booking id when the API returned one, else
  from slot + activity — either way DETERMINISTIC, so the duplicate
  booking_confirmed pushes that can occur across code paths (and tab
  restores) carry the SAME id and GA4 can dedupe on it as transaction_id.

  PSEUDONYMIZATION ONLY: djb2 over "cb:<small integer>" is brute-forceable
  in milliseconds, and a client-side salt would ship in this bundle, adding
  nothing. That satisfies the spec's "Hashat/anonymiserat" for GA4 (the raw
  ClinicBuddy id never reaches the dataLayer), but it is NOT anonymization
  in the GDPR sense — anyone with ClinicBuddy access can re-map ids. If the
  client ever needs real anonymization, hash server-side with a secret salt
  and pass the result to the frontend instead.
*/
export function anonymizedBookingId(state, realBookingId) {
  try {
    const slot = state.selectedSlot || {};
    const datePart = String(slot.date || '').replace(/-/g, '') || '00000000';
    const basis = realBookingId
      ? 'cb:' + String(realBookingId)
      : 'slot:' +
        String(slot.date || '') +
        ' ' +
        String(slot.time || '') +
        ':' +
        String(
          (state.activities &&
            state.activities.selected &&
            state.activities.selected.activity_id) ||
            ''
        );
    /* djb2 */
    let hash = 5381;
    for (let i = 0; i < basis.length; i++) {
      hash = ((hash << 5) + hash + basis.charCodeAt(i)) >>> 0;
    }
    return 'BK-' + datePart + '-' + hash.toString(36);
  } catch (e) {
    return 'BK-00000000-0';
  }
}

/*
  Fallback consultation fee when the caller's own computation came up empty.
  Matters on the orderRef-revisit path (BankID app switch returns the user
  in a new tab): state.infoBox may be empty there, and the caller would
  otherwise report consultation_fee: 0 while the original tab reported the
  real fee. Mirrors the two computations the confirm handlers use — the
  "Konsultationsavgift: X kr" infoBox pattern, then the price list row for
  the selected activity. The fee cannot come from the backend revisit
  payload instead: it is derived client-side and never reaches the server.
*/
function consultationFeeFromState(state) {
  try {
    if (state.infoBox) {
      const match = state.infoBox.match(/Konsultationsavgift:\s*(\d+)\s*kr/i);
      if (match && match[1]) {
        return parseInt(match[1], 10);
      }
    }
    const activity = state.activities && state.activities.selected;
    if (
      activity &&
      state.priceData &&
      Array.isArray(state.priceData.available)
    ) {
      const priceRow = state.priceData.available.find(
        (price) => price && price.activity_id == activity.activity_id
      );
      if (priceRow && priceRow.price) {
        return parseInt(priceRow.price, 10) || 0;
      }
    }
  } catch (e) {}
  return 0;
}

/*
  Spec step 6 (conversion) — pushed alongside the legacy booking_completed
  event (Ads/Meta/Delta untouched). One helper for all three success paths
  (collectBankID, collectBankIDResponseAndSendBooking, bookingWithoutBankID)
  so neither the dedupe key nor the booking-id extraction can drift between
  them. All three endpoints terminate in addBooking's wp_send_json_success,
  so the real ClinicBuddy id lives at response.data.data.booking_id (the
  orderRef-revisit path returns it too, persisted on the order row).
  Dedupe on slot + activity, not booking id, via localStorage (crossTab) —
  the same-device BankID flow returns the user in a new tab and BOTH tabs
  reach a confirm handler; a cross-tab/cross-path duplicate then arrives
  flagged is_revision. The GTM conversion tag must exclude is_revision
  (see header).
*/
export function trackBookingConfirmed(state, { response, consultationPrice }) {
  try {
    const realBookingId =
      (response && response.data && response.data.data
        ? response.data.data.booking_id
        : null) || null;
    trackBookingEvent(
      'booking_confirmed',
      {
        ...funnelParams(state, 6),
        booking_id: anonymizedBookingId(state, realBookingId),
        consultation_fee: consultationPrice || consultationFeeFromState(state),
        currency: 'SEK',
      },
      {
        funnel: true,
        crossTab: true,
        dedupeValue:
          ((state.selectedSlot &&
            state.selectedSlot.date + ' ' + state.selectedSlot.time) ||
            '') +
          ':' +
          ((state.activities &&
            state.activities.selected &&
            state.activities.selected.activity_id) ||
            ''),
      }
    );
  } catch (e) {}
}

/*
  ClinicBuddy's terapeut_sign is a combined "Name Title" string. These rules
  mirror the formatProviderName getter (getters.js) — same replacements in
  the same order, with '|' as the split marker instead of '<br />'. Keep the
  two lists in sync when a provider override is added there.
*/
const PRACTITIONER_TITLE_RULES = [
  ['Sandra Sellström Leg. Sjuksköterska', 'Sandra Sellström Injektionssjuksköterska'],
  ['Silvia Galaz Leg. Sjuksköterska', 'Silvia Galaz Injektionssjuksköterska'],
  ['Anna Hamberg Operationssjuksköterska', 'Anna Hamberg Specialistsjuksköterska'],
  ['Specialistsjuksköterska', '|Specialistsjuksköterska'],
  ['Leg. Läkare. Specialist i plastikkirurgi ', '|Plastikkirurg'],
  ['Leg. Läkare. Specialist i plastikkirurgi', '|Plastikkirurg'],
  ['Leg. Läkare Specialist i plastikkirurgi', '|Plastikkirurg'],
  ['Injektionsspecialist', '|Injektionsspecialist'],
  ['Carolina Ryberg Aukt. Hudterapeut', 'Carolina Ryberg|Certified SkinMap Specialist'],
  ['Aukt. Hudterapeut', '|Hudterapeut'],
  ['Injektionssjuksköterska', '|Injektionssjuksköterska'],
  ['Hudläkare', '|Hudläkare'],
  ['Leg. Sjuksköterska', '|Injektionssjuksköterska'],
  ['Injektionsbehandlare', '|Injektionsbehandlare'],
  ['Anestesisjuksköterska', '|Injektionssjuksköterska'],
  ['Tandläkare', '|Tandläkare'],
  ['LPG-terapeut', '|LPG-terapeut'],
];

/*
  Resolve a provider value into a practitioner name. Providers arrive either
  as a terapeut_id (production config) or as a display string — same duality
  the formatProviderNameInList getter handles. Resolve ids via the cached
  provider meta rows, then split off the title.
*/
export function practitionerNameFromProvider(state, provider) {
  try {
    let sign = provider == null || provider === false ? '' : String(provider);
    const rows =
      state.providers && Array.isArray(state.providers.metaRows)
        ? state.providers.metaRows
        : [];
    if (sign && rows.length) {
      const byId = rows.find(
        (r) => r && String(r.terapeut_id || '').trim() === sign.trim()
      );
      if (byId) {
        sign = String(byId.terapeut_sign || byId.terapeut_name || sign);
      }
    }
    return splitPractitioner(sign).practitioner_name;
  } catch (e) {
    return '';
  }
}

/*
  Report which form fields block a submit attempt (spec: booking_form_field_error).
  Call ONLY on an actual submit attempt, right after the formIsValid getter has
  recomputed state.ui.formErrors — never from the getter itself, which runs on
  every keystroke.
*/
export function trackFormFieldErrors(state) {
  try {
    const fe = (state.ui && state.ui.formErrors) || {};
    const patient = state.patient || {};
    if (fe.ssn || fe.ssnAge || !patient.ssn) {
      trackBookingEvent('booking_form_field_error', { field_name: 'personnummer' });
    }
    if (fe.email || !patient.email) {
      trackBookingEvent('booking_form_field_error', { field_name: 'email' });
    }
    if (fe.phone || !patient.phone) {
      trackBookingEvent('booking_form_field_error', { field_name: 'phone' });
    }
  } catch (e) {}
}

/*
  Split a terapeut_sign into { practitioner_name, practitioner_title }.
  Unknown titles fall back to the full string as name with an empty title.
*/
export function splitPractitioner(terapeutSign) {
  try {
    let str = terapeutSign == null ? '' : String(terapeutSign);
    for (const [from, to] of PRACTITIONER_TITLE_RULES) {
      str = str.replace(from, to);
    }
    const markerIndex = str.indexOf('|');
    if (markerIndex === -1) {
      return { practitioner_name: str.trim(), practitioner_title: '' };
    }
    return {
      practitioner_name: str.slice(0, markerIndex).trim(),
      practitioner_title: str.slice(markerIndex + 1).replace(/\|/g, ' ').trim(),
    };
  } catch (e) {
    return { practitioner_name: '', practitioner_title: '' };
  }
}
